import React from 'react'
import UserMessage from './components/UserMessage'
import PaymentType from './components/PaymentType'
import Table from 'components/Table/index'
import { timeToLocale } from 'components/Helpers/dateToLocale'
import { ActionButton } from 'components/Buttons/index'
import { StatusBadge } from 'components/Badges'
import Amount from 'components/Helpers/Amount'
import { refundBadge, invoiceRefunds } from './components/Refunds'

const url = (invoice) =>
  `/operators/${invoice.operator_id}/members/${invoice.membership_id}/invoices/${invoice.id}`

const getDeclinedActionCode = (payment) => {
  if (!payment.provider_details) return

  return payment.provider_details.action_code || payment.provider_details.reason_code
}

const stringify = (value) => {
  if (typeof value !== 'object') {
    return value;
  }

  let text = null
  for (const key in value) {
    if (text === null) {
      text = `${key}: ${value[key]}`
    } else {
      text += `; ${key}: ${value[key]}`
    }
  }
  return text || value
}

const getDetails = (payment) => {
  if (!payment) return
  if (!payment.payment_method && !payment.provider_details) return

  if (payment.status === 'accepted') {
    if (payment.payment_method && payment.payment_method.details) {
      return stringify(payment.payment_method.details)
    } else {
      return null
    }
  }

  if (payment.provider_details) {
    if (payment.provider_details.additional_data && payment.provider_details.additional_data.acquirer_response_message) {
      return `${payment.provider_details.reason_code}: ${payment.provider_details.additional_data.acquirer_response_message}`
    } else if (payment.provider_details.reason_description) {
      if (payment.provider_details.reason_description.cause) {
        return `${payment.provider_details.reason_code}: ${stringify(payment.provider_details.reason_description.cause)}`
      } else {
        return `${payment.provider_details.reason_code}: ${stringify(payment.provider_details.reason_description)}`
      }
    }
    if (payment.provider_details.error_code) return `${payment.provider_details.error_code}: ${payment.provider_details.error_message}`
    if (payment.provider_details.details) return stringify(payment.provider_details.details)
  }
  if (payment.payment_method && payment.payment_method.details) return stringify(payment.payment_method.details)

  return null
}

// Matches Table's defaultPageSize; below it there is only one page
const PAGE_SIZE = 10

const RefundList = ({ refunds, timezone }) => {
  if (refunds.length === 0) return null

  return (
    <div className='content-box'>
      <Table
        className='refund-list'
        data={refunds}
        showPagination={refunds.length > PAGE_SIZE}
        columns={[
          {
            Header: 'Refund',
            id: 'status',
            Cell: ({ original }) => <StatusBadge {...refundBadge(original)} />
          },
          {
            Header: 'Refunded at',
            id: 'created_at',
            accessor: 'created_at',
            Cell: props =>
              timeToLocale(props.original.created_at, timezone)
          },
          {
            Header: 'Amount',
            id: 'amount',
            Cell: ({ original }) =>
              <Amount amount={original.amount} currency={original.currency} />
          },
          {
            Header: 'Comment',
            id: 'comment',
            Cell: ({ original }) => original.comment
          }
        ]}
        defaultSorted={[
          {
            id: 'created_at',
            desc: true
          }
        ]}
      />
    </div>
  )
}

const InvoicePaymentsView = ({ invoice, loading, timezone }) => {
  if (loading) return null
  const style = {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: '10px'
  }

  return (
    <div className='index'>
      <div className='with-response-margin' style={style}>
        <UserMessage invoice={invoice} timezone={timezone} />
        <ActionButton
          txt='View Invoice'
          icon='report white'
          link={url(invoice)}
        />
      </div>
      <div className='content-box'>
        <Table
          className='payment-list'
          data={invoice.payments}
          loading={loading}
          showPagination={invoice.payments.length > PAGE_SIZE}
          columns={[
            {
              Header: 'Status',
              accessor: 'status',
              Cell: ({ original }) => {
                let { status } = original
                const actionCode = getDeclinedActionCode(original)

                if (actionCode && actionCode !== '0000') { // '0000' is Verifone success code
                  status = `${status} ${actionCode} `
                }

                return <StatusBadge status={status} cssStatus={original.status} />
              }
            },
            {
              Header: 'Charged at',
              Cell: props =>
                timeToLocale(props.original.created_at, timezone)
            },
            {
              Header: 'Payment type',
              Cell: props =>
                <PaymentType paymentMethod={props.original.payment_method} />
            },
            {
              Header: 'Details',
              Cell: props => getDetails(props.original)
            }
          ]}
          defaultSorted={[
            {
              id: 'created_at',
              desc: true
            }
          ]}
        />
      </div>
      <RefundList refunds={invoiceRefunds(invoice.payments)} timezone={timezone} />
    </div>
  )
}

export default InvoicePaymentsView
